import * as Dialog from '@radix-ui/react-dialog';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { Button } from './Button';
import { cn } from './cn';
import { useKeyboardInset } from './useKeyboardInset';

export interface SheetProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  description?: string;
  /**
   * Element to focus when the sheet opens (e.g. a safe "Cancel"). Without it, the first element
   * inside marked `data-autofocus` gets focus, else the first button (Radix's default).
   */
  initialFocus?: RefObject<HTMLElement | null>;
  /**
   * Always visible below the scrolling body (the primary action). It sits above the home indicator
   * and above the on-screen keyboard. A submit button here can name its form with `form="<id>"`.
   */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Phones: a bottom sheet (design.md: slides up in 200ms ease-out, Surface 2, no shadow) that stays
 * above the on-screen keyboard. From 768px: a centered modal. Esc closes it either way (Radix).
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  initialFocus,
  footer,
  children,
}: SheetProps) {
  const kb = useKeyboardInset(open);
  const keyboardOpen = kb.inset > 0;
  const style = {
    '--kb': `${kb.inset}px`,
    ...(kb.visibleHeight ? { '--vvh': `${kb.visibleHeight}px` } : {}),
  } as CSSProperties;
  // The home indicator inset matters only while nothing covers the bottom edge.
  const safeBottom = keyboardOpen ? 'pb-3' : 'pb-[calc(12px+env(safe-area-inset-bottom))]';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="sheet-overlay fixed inset-0 z-40 bg-black/60" />
        <Dialog.Content
          {...(description ? {} : { 'aria-describedby': undefined })}
          data-keyboard={keyboardOpen ? 'open' : undefined}
          style={style}
          onOpenAutoFocus={(e) => {
            const target =
              initialFocus?.current ??
              (e.currentTarget as HTMLElement | null)?.querySelector<HTMLElement>(
                '[data-autofocus]',
              );
            if (target) {
              e.preventDefault();
              target.focus();
            }
          }}
          className={cn(
            'sheet fixed inset-x-0 bottom-[var(--kb)] z-50 mx-auto flex w-full max-w-lg flex-col',
            'max-h-[min(85dvh,calc(var(--vvh,100dvh)-12px))]',
            'rounded-t-[24px] border-t border-divider bg-surface-2 text-fg',
            'md:inset-x-auto md:top-1/2 md:bottom-auto md:left-1/2 md:max-h-[min(85dvh,760px)]',
            'md:w-[calc(100vw-48px)] md:max-w-xl md:-translate-x-1/2 md:-translate-y-1/2',
            'md:rounded-[24px] md:border',
          )}
        >
          <div className="flex shrink-0 items-center justify-between gap-2 px-4 pt-4 pb-3 md:px-6 md:pt-5">
            <Dialog.Title className="text-xl font-bold">{title}</Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="ghost">Close</Button>
            </Dialog.Close>
          </div>
          <div
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 md:px-6',
              footer ? 'pb-2' : `${safeBottom} md:pb-6`,
            )}
          >
            {description ? (
              <Dialog.Description className="mb-3 text-sm text-muted">
                {description}
              </Dialog.Description>
            ) : null}
            {children}
          </div>
          {footer ? (
            <div
              data-testid="sheet-footer"
              className={cn(
                'shrink-0 border-t border-divider px-4 pt-3 md:px-6 md:pb-5',
                safeBottom,
              )}
            >
              {footer}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
